import { useLayoutEffect, useRef } from 'react';
import { ConvertTraditional, TranslateLanguage } from './KeywordTranslator';

const chineseText = /[\u3400-\u9fff]/;
const ignoredContent = 'textarea, script, style, code, pre, [contenteditable="true"], [data-admin-language-original]';
const translatedAttributes = ['placeholder', 'title', 'aria-label', 'alt'];

// Legacy admin sections contain both simplified and traditional labels. Translate only
// rendered text and accessibility labels; never change input values or saved records.
export default function AdminLanguageBoundary({ language, children }) {
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const translate = value => {
      if (!value || !chineseText.test(value)) return value;
      return language === 'S' ? TranslateLanguage('S', value) : ConvertTraditional(value);
    };
    const isIgnored = element => element?.closest(ignoredContent);
    const translateText = node => {
      if (isIgnored(node.parentElement)) return;
      const translated = translate(node.nodeValue);
      if (translated !== node.nodeValue) node.nodeValue = translated;
    };
    const translateAttributes = element => {
      if (isIgnored(element)) return;
      translatedAttributes.forEach(name => {
        const value = element.getAttribute(name);
        if (value === null) return;
        const translated = translate(value);
        if (translated !== value) element.setAttribute(name, translated);
      });
    };
    const translateTree = node => {
      if (node.nodeType === Node.TEXT_NODE) {
        translateText(node);
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE || isIgnored(node)) return;
      translateAttributes(node);
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const current = walker.currentNode;
        if (current.nodeType === Node.TEXT_NODE) translateText(current);
        else if (!isIgnored(current)) translateAttributes(current);
      }
    };

    translateTree(root);
    const observer = new MutationObserver(records => {
      records.forEach(record => {
        if (record.type === 'characterData') translateText(record.target);
        else if (record.type === 'attributes') translateAttributes(record.target);
        else record.addedNodes.forEach(translateTree);
      });
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: translatedAttributes,
    });
    return () => observer.disconnect();
  }, [language]);

  return <div ref={rootRef} className="admin-dashboard-shell">{children}</div>;
}
